import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TSingleCase } from './get-single-case.api';

/**
 *
 */
export type TGetCustomerProfileFromCaseResponse = ApiResponse<TSingleCase>;
export type TGetCustomerProfileFromCaseParams = {
  caseId: string;
};

export const getCustomerProfileFromCase = async (
  params: TGetCustomerProfileFromCaseParams
): Promise<TGetCustomerProfileFromCaseResponse> => {
  const url = `case/${params.caseId}`;

  return axiosInstance.get(url);
};

/**
 * Use Get Detail Case React-Query Hook
 */
export const GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY = 'customer-profile-from-case';

export const getCustomerProfileFromCaseQueryOptions = <TData = TGetCustomerProfileFromCaseResponse>(
  params: TGetCustomerProfileFromCaseParams,
  select?: (data: TGetCustomerProfileFromCaseResponse) => TData
) => {
  return queryOptions({
    queryKey: [GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, params?.caseId],
    queryFn: () => getCustomerProfileFromCase(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useCustomerProfileFromCaseQuery<TData>(options: {
  params: TGetCustomerProfileFromCaseParams;
  queryConfig?: QueryConfig<typeof getCustomerProfileFromCaseQueryOptions<TData>>;
  select: (data: TGetCustomerProfileFromCaseResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useCustomerProfileFromCaseQuery(options: {
  params: TGetCustomerProfileFromCaseParams;
  queryConfig?: QueryConfig<typeof getCustomerProfileFromCaseQueryOptions<TGetCustomerProfileFromCaseResponse>>;
}): ReturnType<typeof useQuery<TGetCustomerProfileFromCaseResponse>>;

// Implementation
export function useCustomerProfileFromCaseQuery<TData = TGetCustomerProfileFromCaseResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetCustomerProfileFromCaseParams;
  queryConfig?: QueryConfig<typeof getCustomerProfileFromCaseQueryOptions<TData | TGetCustomerProfileFromCaseResponse>>;
  select?: (data: TGetCustomerProfileFromCaseResponse) => TData;
}) {
  return useQuery({
    ...getCustomerProfileFromCaseQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetCustomerProfileFromCaseResponse ? TGetCustomerProfileFromCaseResponse : TData>
  >;
}
